/**
 * expandEditable — universal inline-editability pre-pass.
 *
 * The builder's edit runtime discovers editable fields by walking the DOM for
 * `data-builder-field` markers. Historically those markers (and the editor
 * type) were authored in two places that had to stay in sync: a `data-builder-field`
 * attribute in the rjBuild, AND a separate `fields[]` manifest on the block def.
 *
 * The universal format collapses this to a single, self-describing source: a
 * render node declares its own editability inline via an `editable:` key, e.g.
 *
 *   - type: h2
 *     editable: { field: title, type: text, label: 'Titre' }
 *     content: ~~.title
 *
 * reactive-json only forwards a node's `attributes:` to the DOM — an unknown
 * top-level key like `editable:` is silently dropped (see lib `Html.jsx`).
 * `expandEditable` therefore runs once, at normalize time, and rewrites every
 * `editable:` declaration into the `data-*` attributes the runtime reads:
 *
 *   data-builder-field   = editable.field
 *   data-builder-type    = editable.type ?? editable.editorType
 *   data-builder-options = JSON of the remaining editor options (only if any)
 *   class               += ' builder-editable-field'   (for the hover outline)
 *
 * A declaration carrying a `column:` (a cell of a list row, inside a Switch
 * template) is instead rewritten into a `BuilderRowField` primitive — see
 * `toRowField` below.
 *
 * Second, symmetrical pass — the TEXTE INDICATIF (placeholder) of a form field.
 * An `<input>` has no text node to turn contentEditable, and its placeholder is
 * already declared: `inputAttributes: { placeholder: ~~.….placeholderEmail }`.
 * That declaration IS the editability statement (it names a field of the unit),
 * so nothing has to be re-authored — `placeholderFieldOf` reads the path against
 * the enclosing unit's `dataBaseLocation` and stamps the resolved key as
 * `data-builder-placeholder-field` on the control. The runtime
 * (`BuilderBlockWrapper`) then edits THAT field, in the control itself, instead
 * of letting an editor fill the form in. A placeholder that is a literal, or a
 * path pointing anywhere else than a top-level field of the unit, is left alone:
 * the control keeps the live behaviour it has today (facet, search preview).
 *
 * Troisième passe, du même esprit — les LIAISONS AU CONTENU d'une page-modèle.
 * Un nœud qui affiche `~~._context_.<clé>` déclare, par ce seul chemin, qu'il
 * montre un champ de l'objet servi à cette URL. Le chemin est estampillé en
 * `data-builder-context-field` ; il ne rend rien éditable par lui-même (en
 * rendu public comme en édition de PAGE l'attribut est inerte) — c'est
 * l'éditeur d'objet, qui seul connaît le type courant et son mapping, qui
 * décide au clic si ce point tombe sur un champ réel (`resolveContextField`).
 *
 * Design contract:
 *  - PURE & immutable: never mutates its input; returns new objects. This keeps
 *    it idempotent and safe to call on shared registry data.
 *  - NON-DESTRUCTIVE: a node without `editable:` is returned deep-equal. A build
 *    with zero `editable:` keys round-trips unchanged (golden-master guarantee).
 *  - NON-CLOBBERING: an existing hand-authored `data-builder-field` is never
 *    overwritten, so legacy blocks keep working alongside the inline format.
 *  - IDEMPOTENT: the `editable:` key is consumed (removed) on expansion, so a
 *    second pass is a no-op.
 */

import { pickEditorOptions } from './editorOptions'
import { contextPathOf } from './contextFields'

const EDITABLE_CLASS = 'builder-editable-field'

const isPlainObject = (v) =>
  v != null && typeof v === 'object' && !Array.isArray(v)

/** Append `builder-editable-field` to a className string without duplicating. */
const withEditableClass = (className) => {
  const base = typeof className === 'string' ? className : ''
  const classes = base.split(/\s+/).filter(Boolean)
  if (classes.includes(EDITABLE_CLASS)) return base || EDITABLE_CLASS
  classes.push(EDITABLE_CLASS)
  return classes.join(' ')
}

/**
 * Build the `attributes` object for a node carrying `editable:`, merging onto
 * any attributes the node already declares. Never clobbers an existing
 * `data-builder-field`.
 */
const applyEditable = (attributes, editable) => {
  const attrs = isPlainObject(attributes) ? { ...attributes } : {}

  if (editable.field != null && attrs['data-builder-field'] == null) {
    attrs['data-builder-field'] = editable.field
  }

  const type = editable.type ?? editable.editorType
  if (type != null && attrs['data-builder-type'] == null) {
    attrs['data-builder-type'] = type
  }

  const options = pickEditorOptions(editable)
  if (Object.keys(options).length > 0 && attrs['data-builder-options'] == null) {
    attrs['data-builder-options'] = JSON.stringify(options)
  }
  if (options.placeholder != null && attrs['data-placeholder'] == null) {
    attrs['data-placeholder'] = options.placeholder
  }

  attrs.class = withEditableClass(attrs.class)
  return attrs
}

/**
 * A cell of a list row — `editable: { field, column, type }` — can't be
 * expanded to static attributes: the row index only exists at render time.
 * The node is rewritten into a `BuilderRowField` primitive (its original
 * `type` becomes the rendered `tag`), which derives the row path from the
 * store and emits the same `data-builder-*` markers with the row baked in.
 *
 * One carrier isn't an HTML tag: `BuilderHtmlField`, the rich-text renderer.
 * A rich-text COLUMN is authored on it exactly like a whole rich-text field,
 * and is rewritten into a `div` cell flagged `html: true` — `BuilderRowField`
 * then injects the value as HTML instead of rendering it as children. Without
 * this the cell would render the tag name `BuilderHtmlField` as an unknown
 * element and print the markup as escaped text.
 */
const HTML_CARRIERS = { BuilderHtmlField: 'div' }

const toRowField = (node, editable) => {
  const { editable: _consumed, type, content, ...rest } = node
  const htmlTag = HTML_CARRIERS[type]
  const out = {
    ...rest,
    type: 'BuilderRowField',
    tag: htmlTag ?? type ?? 'span',
    field: editable.field,
    column: editable.column,
    editorType: editable.type ?? editable.editorType,
    options: pickEditorOptions(editable),
  }
  if (htmlTag) out.html = true
  if (content !== undefined) out.content = content
  return out
}

/**
 * The keys under which reactive-json nests a CHILD rjBuild fragment.
 *
 * `content` is the universal one. `label` is the form-field slot: the library's
 * `Input` / `TextAreaField` / `SelectField` / `CheckBoxField` render `props.label`
 * through `<View>` (not as a plain string), so a field's `<label>` can host real
 * nodes — and therefore an `editable:` declaration. That is what makes a form's
 * *intitulés* editable IN PLACE (inline UX rule) instead of through a row of
 * `builder-edit-only` pills parked next to the form.
 *
 * Anything else (a primitive's scalar `label`, `options[].label`, …) is left
 * alone: `expandNode` is a no-op on scalars, so listing a slot only ever costs
 * a walk.
 */
const FRAGMENT_SLOTS = ['content', 'label']

/**
 * Where a subtree's data lives. A `BuilderUnit` declares it
 * (`dataBaseLocation: ~~.apiData.data.objectData.data`); outside any unit the
 * store root `~~` is the base. It is the SAME base a field key is resolved
 * against when the runtime commits (`commitField(fieldKey, …)`) — hence only a
 * path of the form `<base>.<key>` (ONE segment, no dot) yields a field key.
 */
const DEFAULT_DATA_BASE = '~~'

/** The unit field a form control's placeholder is bound to, or null. */
const placeholderFieldOf = (node, base) => {
  const declared = isPlainObject(node.inputAttributes)
    ? node.inputAttributes.placeholder ?? node.placeholder
    : node.placeholder
  if (typeof declared !== 'string') return null
  const prefix = base.endsWith('.') ? base : `${base}.`
  if (!declared.startsWith(prefix)) return null
  const key = declared.slice(prefix.length)
  return /^[A-Za-z0-9_-]+$/.test(key) ? key : null
}

/** Stamp the resolved key on the CONTROL's own attributes (not the wrapper's). */
const applyPlaceholderField = (node, key) => {
  const attrs = isPlainObject(node.inputAttributes) ? { ...node.inputAttributes } : {}
  if (attrs['data-builder-placeholder-field'] != null) return node
  attrs['data-builder-placeholder-field'] = key
  return { ...node, inputAttributes: attrs }
}

/**
 * Le chemin d'objet qu'un nœud AFFICHE. `content:` couvre le texte rendu,
 * `value:` les primitives qui reçoivent la valeur entière (`LazyImage`).
 * Un chemin RELATIF (`~.name`, dans un `Switch`) n'en est pas un : la ligne
 * n'existe qu'au rendu — c'est le domaine des cellules (`BuilderRowField`).
 */
const contextFieldOf = (node) => contextPathOf(node.content) ?? contextPathOf(node.value)

/** Stamp the context path on the node's own attributes. */
const applyContextField = (node, path) => {
  const attrs = isPlainObject(node.attributes) ? { ...node.attributes } : {}
  if (attrs['data-builder-context-field'] != null) return node
  attrs['data-builder-context-field'] = path
  return { ...node, attributes: attrs }
}

/**
 * Recursively transform one rjBuild node. Children live under the fragment
 * slots (array of nodes, a single node, or a scalar/template string). Other
 * keys (attributes, value, name, style, template paths…) are copied as-is.
 */
const expandNode = (node, base = DEFAULT_DATA_BASE) => {
  // Scalars and template strings ('~~.title', ' Background', numbers) pass through.
  if (!isPlainObject(node)) return node

  let out = node

  if (isPlainObject(node.editable)) {
    if (node.editable.column != null && node.editable.field != null) {
      out = toRowField(node, node.editable)
    } else {
      const { editable, ...rest } = node
      out = { ...rest, attributes: applyEditable(node.attributes, editable) }
    }
  }

  // A unit re-bases everything under it (its own `dataBaseLocation`).
  const childBase = typeof node.dataBaseLocation === 'string' && node.dataBaseLocation
    ? node.dataBaseLocation
    : base

  const placeholderField = placeholderFieldOf(out, childBase)
  if (placeholderField) out = applyPlaceholderField(out, placeholderField)

  const contextPath = contextFieldOf(out)
  if (contextPath) out = applyContextField(out, contextPath)

  for (const slot of FRAGMENT_SLOTS) {
    if (slot in out) out = { ...out, [slot]: expandContent(out[slot], childBase) }
  }

  return out
}

/** Transform a node's `content`, which may be an array, a node, or a scalar. */
const expandContent = (content, base = DEFAULT_DATA_BASE) => {
  if (Array.isArray(content)) return content.map((child) => expandNode(child, base))
  if (isPlainObject(content)) return expandNode(content, base)
  return content
}

/**
 * Expand a full rjBuild. Walks `renderView` (array of nodes) and every entry of
 * `templates` (a map of name → node). `data` and any other top-level keys are
 * preserved untouched.
 *
 * @param {object|null|undefined} build
 * @returns {object|null|undefined} a new build with `editable:` expanded
 */
export const expandEditable = (build) => {
  if (!isPlainObject(build)) return build

  const out = { ...build }

  if (Array.isArray(build.renderView)) {
    out.renderView = build.renderView.map((node) => expandNode(node))
  }

  if (isPlainObject(build.templates)) {
    const templates = {}
    for (const [name, tpl] of Object.entries(build.templates)) {
      templates[name] = expandNode(tpl)
    }
    out.templates = templates
  }

  return out
}

export default expandEditable
